import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  FiHeart,
  FiFileText,
  FiGift,
  FiCalendar,
  FiBriefcase,
  FiUsers,
  FiShoppingBag,
  FiGrid,
  FiActivity,
  FiArrowRight,
  FiMapPin,
  FiCheckCircle,
  FiMaximize,
  FiTag,
  FiStar,
  FiPlus,
  FiEdit3,
  FiFolder,
} from "react-icons/fi";
import SaaSStatCard from "../../common/saas/SaaSStatCard";
import SaaSEmptyState from "../../common/saas/SaaSEmptyState";
import SaaSLoadingSkeleton from "../../common/saas/SaaSLoadingSkeleton";
import SaaSSectionHeader from "../../common/saas/SaaSSectionHeader";
import {
  getCustomerLeads,
  getMeetingReminders,
} from "../../../api/customer/customerFeatureApis";
import "./CustomerDashboardView.css";

export const CustomerDashboardView = () => {
  const navigate = useNavigate();
  const authState = useSelector((state) => state.auth);
  const loggedInUser = authState?.user ?? authState;
  const token =
    loggedInUser?.refreshToken ||
    authState?.refreshToken ||
    loggedInUser?.token ||
    authState?.token;

  const baseUrl = process.env.REACT_APP_BASE_URL;
  const assetUrl = process.env.REACT_APP_ASSET_URL || "";

  const [greeting, setGreeting] = useState("Good Morning");
  const [properties, setProperties] = useState([]);
  const [posts, setPosts] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [leadsCount, setLeadsCount] = useState(0);
  const [remindersCount, setRemindersCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Derive Display Name
  const userObj = loggedInUser?.user || loggedInUser?.userDetails || loggedInUser;
  const rawFirstName = userObj?.first_name || userObj?.firstName;
  const rawLastName = userObj?.last_name || userObj?.lastName;
  const rawName = userObj?.name || userObj?.customer_name;
  const fullName = [rawFirstName, rawLastName].filter(Boolean).join(" ") || rawName;
  const displayName = fullName && fullName.trim() && !fullName.toLowerCase().includes("null")
    ? fullName.trim()
    : "Ayush";

  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) setGreeting("Good Morning");
    else if (hour < 18) setGreeting("Good Afternoon");
    else setGreeting("Good Evening");
  }, []);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      try {
        // 1. Fetch properties
        try {
          const propRes = await axios.post(
            `${baseUrl}/property/list/preferred/v1`,
            { page_number: 1, page_size: 10, order_by: "DESC", keyword_search: "" },
            { headers }
          );
          const propList = propRes.data?.propertyAssets || propRes.data?.properties || [];
          if (propList.length > 0) {
            setProperties(propList);
          } else {
            const fallbackRes = await axios.post(
              `${baseUrl}/property/list`,
              { page_number: 1, page_size: 10, order_by: "DESC" },
              { headers }
            );
            setProperties(fallbackRes.data?.properties || fallbackRes.data?.propertyAssets || []);
          }
        } catch (e) {
          console.warn("Customer dashboard property fetch error:", e);
        }

        // 2. Fetch posts / inquiries
        try {
          const prefPostRes = await axios.post(
            `${baseUrl}/post/list/preferred`,
            { service: "", state: "", city: "", keyword_search: "", page_number: 1, page_size: 10, order_by: "DESC" },
            { headers }
          );
          setPosts(prefPostRes.data?.postAssets || prefPostRes.data?.leads || []);
        } catch (e) {
          console.warn("Customer dashboard posts fetch error:", e);
        }

        // 3. Fetch customer leads count
        if (token) {
          try {
            const leadsData = await getCustomerLeads(token, { page_number: 1, page_size: 5 });
            const list = leadsData?.customerLeads || leadsData?.leads || [];
            setLeadsCount(leadsData?.totalRecord || list.length || 0);
          } catch (e) {
            console.warn("Error fetching customer leads count:", e);
          }

          // 4. Fetch reminders count
          try {
            const remData = await getMeetingReminders(token, { pageNumber: 1, pageSize: 5 });
            const remList = remData?.reminders || remData?.data || [];
            setRemindersCount(remData?.totalRecord || remList.length || 0);
          } catch (e) {
            console.warn("Error fetching reminders count:", e);
          }
        }

        // 5. Fetch Promotions
        try {
          const promoRes = await axios.post(
            `${baseUrl}/promotion/`,
            { page_number: 1, page_size: 8, order_by: "DESC" },
            { headers }
          );
          setPromotions(promoRes.data?.promotions || promoRes.data?.data || []);
        } catch (e) {
          console.warn("Customer dashboard promotions fetch error:", e);
        }
      } catch (err) {
        console.error("Dashboard data load error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [token, baseUrl]);

  return (
    <div className="customer-dash-root">
      {/* 1. Header / Welcome Banner */}
      <div className="customer-dash-welcome">
        <div className="customer-dash-welcome__left">
          <div className="customer-dash-welcome__badge">
            <FiCheckCircle size={13} /> Valued Homebuyer Workspace
          </div>
          <h1 className="customer-dash-welcome__title">
            {greeting}, {displayName}!
          </h1>
          <p className="customer-dash-welcome__subtitle">
            Welcome to your customer portal. Search verified properties, track your inquiries, schedule visits, and earn Desire reward points.
          </p>
        </div>
        <div className="customer-dash-welcome__actions">
          <button
            type="button"
            className="btn btn-primary btn-sm px-3 py-2 rounded-3 fw-medium shadow-sm d-inline-flex align-items-center gap-2"
            onClick={() => navigate("/add-property")}
          >
            <FiPlus size={15} />
            <span>Create Property</span>
          </button>
          <button
            type="button"
            className="btn btn-outline-primary btn-sm px-3 py-2 rounded-3 fw-medium bg-white shadow-sm d-inline-flex align-items-center gap-2"
            onClick={() => navigate("/post/add-post")}
          >
            <FiEdit3 size={15} />
            <span>Create Post</span>
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm px-3 py-2 rounded-3 fw-medium bg-white shadow-sm d-inline-flex align-items-center gap-2"
            onClick={() => navigate("/customer/myspace")}
          >
            <FiFolder size={15} className="text-secondary" />
            <span>MySpace</span>
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm px-3 py-2 rounded-3 fw-medium bg-white shadow-sm d-inline-flex align-items-center gap-2"
            onClick={() => navigate("/customer/properties")}
          >
            <FiBriefcase size={15} className="text-secondary" />
            <span>Browse Properties</span>
          </button>
        </div>
      </div>

      {/* 2. Customer KPI Stat Cards */}
      {loading ? (
        <SaaSLoadingSkeleton type="stats" count={4} />
      ) : (
        <div className="row g-3">
          <div className="col-12 col-sm-6 col-lg-3">
            <SaaSStatCard
              title="MySpace Content"
              value={properties.length > 0 ? `${properties.length} Items` : "0"}
              icon={<FiFolder />}
              color="blue"
              subtitle="Created & Saved Content"
              onClick={() => navigate("/customer/myspace")}
            />
          </div>
          <div className="col-12 col-sm-6 col-lg-3">
            <SaaSStatCard
              title="My Inquiries"
              value={leadsCount || "0"}
              icon={<FiFileText />}
              color="green"
              subtitle="Active Requirements"
              onClick={() => navigate("/customer/leads")}
            />
          </div>
          <div className="col-12 col-sm-6 col-lg-3">
            <SaaSStatCard
              title="Desire Rewards"
              value="2,450 Pts"
              icon={<FiGift />}
              color="amber"
              subtitle="Gold Loyalty Tier"
              onClick={() => navigate("/customer/rewards")}
            />
          </div>
          <div className="col-12 col-sm-6 col-lg-3">
            <SaaSStatCard
              title="Scheduled Meetings"
              value={remindersCount || "0"}
              icon={<FiCalendar />}
              color="purple"
              subtitle="Site Visits & Calls"
              onClick={() => navigate("/customer/reminders")}
            />
          </div>
        </div>
      )}

      {/* 3. Customer Loyalty Rewards & Profile Card */}
      <div className="customer-dash-rewards-banner">
        <div className="customer-dash-rewards-banner__header">
          <span className="customer-dash-rewards-banner__badge">
            <FiStar fill="#fbbf24" color="#fbbf24" size={14} /> Gold Loyalty Tier
          </span>
          <span className="customer-dash-rewards-banner__id">ID: CUST-8821</span>
        </div>
        <div className="customer-dash-rewards-banner__balance">
          2,450 <span style={{ fontSize: "1rem", fontWeight: 600 }}>DESIRE POINTS</span>
        </div>
        <p className="customer-dash-rewards-banner__points-label">
          Earn points with every site visit, property inquiry, and verified review. Points never expire.
        </p>

        <div className="customer-dash-rewards-banner__progress-wrap">
          <div className="customer-dash-rewards-banner__progress-bar">
            <div
              className="customer-dash-rewards-banner__progress-fill"
              style={{ width: "82%" }}
            />
          </div>
          <div className="customer-dash-rewards-banner__progress-text">
            <span>Progress to Platinum Tier</span>
            <span>82% • 550 pts to go</span>
          </div>
        </div>

        <div className="d-flex flex-wrap gap-2 align-items-center">
          <button
            type="button"
            className="customer-dash-rewards-banner__btn"
            onClick={() => navigate("/customer/rewards")}
          >
            <FiGift size={16} />
            <span>Redeem Points & Vouchers</span>
          </button>
          <button
            type="button"
            className="btn btn-outline-light btn-sm px-3 py-2 rounded-2 fw-medium d-inline-flex align-items-center gap-1"
            onClick={() => navigate("/customer/profile")}
          >
            <span>View Profile & Email Verification</span>
            <FiArrowRight size={14} />
          </button>
        </div>
      </div>

      {/* 4. Customer Services & Communities Quick Hub */}
      <div className="customer-dash-section">
        <div className="card border-0 shadow-sm rounded-3 p-3 bg-white">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h6 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                <FiGrid className="text-primary" /> Customer Marketplace & Community Hub
              </h6>
              <small className="text-muted">Instant access to your home services and social directories</small>
            </div>
          </div>
          <div className="row g-2">
            <div className="col-6 col-md-4 col-lg-2">
              <button
                className="customer-dash-hub-btn"
                onClick={() => navigate("/customer/services")}
              >
                <div className="customer-dash-hub-icon bg-primary bg-opacity-10 text-primary">
                  <FiGrid size={20} />
                </div>
                <span className="customer-dash-hub-label">Home Services</span>
              </button>
            </div>
            <div className="col-6 col-md-4 col-lg-2">
              <button
                className="customer-dash-hub-btn"
                onClick={() => navigate("/customer/bazaar")}
              >
                <div className="customer-dash-hub-icon bg-success bg-opacity-10 text-success">
                  <FiShoppingBag size={20} />
                </div>
                <span className="customer-dash-hub-label">Local Bazaar</span>
              </button>
            </div>
            <div className="col-6 col-md-4 col-lg-2">
              <button
                className="customer-dash-hub-btn"
                onClick={() => navigate("/customer/communities")}
              >
                <div className="customer-dash-hub-icon bg-info bg-opacity-10 text-info">
                  <FiUsers size={20} />
                </div>
                <span className="customer-dash-hub-label">Communities</span>
              </button>
            </div>
            <div className="col-6 col-md-4 col-lg-2">
              <button
                className="customer-dash-hub-btn"
                onClick={() => navigate("/customer/rewards")}
              >
                <div className="customer-dash-hub-icon bg-warning bg-opacity-10 text-warning">
                  <FiGift size={20} />
                </div>
                <span className="customer-dash-hub-label">Desire Rewards</span>
              </button>
            </div>
            <div className="col-6 col-md-4 col-lg-2">
              <button
                className="customer-dash-hub-btn"
                onClick={() => navigate("/customer/reminders")}
              >
                <div className="customer-dash-hub-icon bg-danger bg-opacity-10 text-danger">
                  <FiCalendar size={20} />
                </div>
                <span className="customer-dash-hub-label">Visit Reminders</span>
              </button>
            </div>
            <div className="col-6 col-md-4 col-lg-2">
              <button
                className="customer-dash-hub-btn"
                onClick={() => navigate("/customer/healthians")}
              >
                <div className="customer-dash-hub-icon bg-secondary bg-opacity-10 text-secondary">
                  <FiActivity size={20} />
                </div>
                <span className="customer-dash-hub-label">Care Leads</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 5. Recommended & Saved Properties Section */}
      <div className="customer-dash-section">
        <SaaSSectionHeader
          title="Recommended Verified Properties"
          subtitle="Hand-picked residential listings based on your housing preferences"
          linkTo="/customer/properties"
          linkLabel="Explore All Properties"
          badge={properties.length > 0 ? `${properties.length} Available` : null}
        />

        {loading ? (
          <SaaSLoadingSkeleton type="card" count={3} />
        ) : properties.length > 0 ? (
          <div className="saas-cards-horizontal-scroll">
            {properties.map((prop, idx) => {
              const imageSrc =
                prop.propertyImages?.[0]?.image_url
                  ? prop.propertyImages[0].image_url.startsWith("http")
                    ? prop.propertyImages[0].image_url
                    : `${assetUrl}${prop.propertyImages[0].image_url}`
                  : `https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=600&auto=format&fit=crop&q=80`;

              const budgetDisplay = prop.budget
                ? `₹${Number(prop.budget).toLocaleString("en-IN")}`
                : "Price on Request";

              return (
                <div
                  key={prop.id || idx}
                  className="saas-property-card"
                  onClick={() => navigate(`/customer/properties/${prop.id || ""}`)}
                >
                  <div className="saas-property-card__img-wrap">
                    <img src={imageSrc} alt={prop.title || "Property"} loading="lazy" />
                    <span className="saas-property-card__badge-purpose">
                      {prop.purpose_name || "For Sale"}
                    </span>
                    <span className="saas-property-card__badge-price">{budgetDisplay}</span>
                  </div>
                  <div className="saas-property-card__body">
                    <h3 className="saas-property-card__title">
                      {prop.title || prop.property_name || "Modern Residential Property"}
                    </h3>
                    <p className="saas-property-card__location">
                      <FiMapPin size={13} className="text-primary flex-shrink-0" />
                      <span>
                        {[prop.area_name, prop.city_name, prop.state_name].filter(Boolean).join(", ") || "Prime Location"}
                      </span>
                    </p>
                    <div className="saas-property-card__specs">
                      {prop.type_name && (
                        <span className="saas-spec-pill">
                          <FiGrid size={12} /> {prop.type_name}
                        </span>
                      )}
                      {prop.sub_type_name && (
                        <span className="saas-spec-pill">
                          <FiMaximize size={12} /> {prop.sub_type_name}
                        </span>
                      )}
                      <span className="saas-spec-pill saas-spec-pill--highlight">
                        <FiCheckCircle size={12} /> Verified
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <SaaSEmptyState
            icon={<FiHeart />}
            title="No Saved Properties Yet"
            description="Properties you save will appear here so you can easily revisit them."
            actionLabel="Browse Properties"
            actionLink="/customer/properties"
          />
        )}
      </div>

      {/* 6. Recent Community Inquiries Section */}
      <div className="customer-dash-section">
        <SaaSSectionHeader
          title="Community Discussions & Inquiries"
          subtitle="Explore questions, recommendations, and local insights from homeowners"
          linkTo="/customer/posts"
          linkLabel="View All Posts"
          badge={posts.length > 0 ? `${posts.length} Posts` : null}
        />

        {loading ? (
          <SaaSLoadingSkeleton type="card" count={3} />
        ) : posts.length > 0 ? (
          <div className="saas-cards-horizontal-scroll">
            {posts.map((post, idx) => {
              const authorName =
                post.customer_name ||
                [post.customer?.first_name, post.customer?.last_name].filter(Boolean).join(" ") ||
                "Community Member";
              const initial = authorName.charAt(0).toUpperCase() || "C";
              const serviceName = post.service || post.service_name || "Housing Inquiry";
              const locationText = [post.city_name, post.state_name].filter(Boolean).join(", ");

              return (
                <div
                  key={post.id || idx}
                  className="saas-post-card"
                  onClick={() => navigate(`/customer/posts/${post.id || ""}`)}
                >
                  <div className="saas-post-card__header">
                    <div className="saas-post-card__avatar">{initial}</div>
                    <div className="saas-post-card__meta">
                      <h4 className="saas-post-card__author">{authorName}</h4>
                      <span className="saas-post-card__tag">{serviceName}</span>
                    </div>
                  </div>
                  <div className="saas-post-card__body">
                    <p className="saas-post-card__desc">
                      {post.description
                        ? post.description.replace(/<[^>]+>/g, "").substring(0, 140)
                        : "Looking for verified property advice in this area..."}
                      {post.description && post.description.length > 140 ? "..." : ""}
                    </p>
                    {locationText && (
                      <div className="saas-post-card__location">
                        <FiMapPin size={12} /> {locationText}
                      </div>
                    )}
                  </div>
                  <div className="saas-post-card__footer">
                    <button
                      type="button"
                      className="saas-btn saas-btn--outline saas-btn--sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/customer/posts/${post.id || ""}`);
                      }}
                    >
                      <span>Read Inquiry</span>
                      <FiArrowRight size={12} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <SaaSEmptyState
            icon={<FiFileText />}
            title="No Inquiries Yet"
            description="Your property inquiries will appear here when you contact a property partner."
            actionLabel="Browse Properties"
            actionLink="/customer/properties"
          />
        )}
      </div>

      {/* 7. Active Promotions Section */}
      <div className="customer-dash-section">
        <SaaSSectionHeader
          title="Featured Promotions & Member Offers"
          subtitle="Exclusive partner discounts, furnishings offers, and interior vouchers"
          linkTo="/customer/promotions"
          linkLabel="View All Offers"
        />

        {loading ? (
          <SaaSLoadingSkeleton type="card" count={3} />
        ) : promotions.length > 0 ? (
          <div className="saas-cards-horizontal-scroll">
            {promotions.map((promo, idx) => {
              const imageSrc =
                promo.propertyImages?.[0]?.image_url
                  ? promo.propertyImages[0].image_url.startsWith("http")
                    ? promo.propertyImages[0].image_url
                    : `${assetUrl}${promo.propertyImages[0].image_url}`
                  : promo.image_url || `https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=600&auto=format&fit=crop&q=80`;

              return (
                <div
                  key={promo.id || idx}
                  className="saas-promo-card"
                  onClick={() => navigate(`/customer/promotions/${promo.id || ""}`)}
                >
                  <div className="saas-promo-card__img-wrap">
                    <img src={imageSrc} alt={promo.title || "Offer"} loading="lazy" />
                    <span className="saas-promo-card__tag">
                      <FiTag size={12} /> Special Offer
                    </span>
                  </div>
                  <div className="saas-promo-card__body">
                    <h3 className="saas-promo-card__title">
                      {promo.title || promo.promotion_name || "Exclusive Homebuyer Discount"}
                    </h3>
                    <p className="saas-promo-card__desc">
                      {promo.description
                        ? promo.description.replace(/<[^>]+>/g, "").substring(0, 110)
                        : "Special featured savings on interior designs and verified home services."}
                      ...
                    </p>
                    <div className="d-flex align-items-center justify-content-between mt-auto pt-2 border-top">
                      <span className="text-primary fw-bold" style={{ fontSize: "0.8125rem" }}>
                        Limited Time
                      </span>
                      <span className="saas-link-arrow">
                        Explore <FiArrowRight size={13} />
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <SaaSEmptyState
            icon={<FiTag />}
            title="No Active Member Offers"
            description="Check back soon for seasonal discounts, shopping vouchers, and local partner promotions."
          />
        )}
      </div>
    </div>
  );
};

export default CustomerDashboardView;
